<template>
    <!--
        Przy prezentacji DOM jest taki sam jak w /PublicSession (bez dodatkowego opakowania), żeby selektory
        CSS klientów działały w obu wejściach. Komunikat o utracie połączenia trafia do slotu prezentacji.
    -->
    <SessionVisualization v-if="presentation !== null"
                          ref="presentation"
                          :key="presentationKey"
                          :initialData="presentation"
                          :loadSessionData="loadPresentedSession"
                          :confirmTarget="confirmPresentedTarget">
        <div v-if="isConnectionLost" class="visualization-connection-lost" role="status">
            {{ $t('com.file.visualization.connection-lost') }}
        </div>
    </SessionVisualization>
    <section v-else class="visualization-idle">
        <VisualizationHeader :client="clientName" />
        <section class="visualization-idle__body" v-if="statusMessage">
            <p class="visualization-idle__message">{{ statusMessage }}</p>
        </section>
        <div v-if="isConnectionLost" class="visualization-connection-lost" role="status">
            {{ $t('com.file.visualization.connection-lost') }}
        </div>
    </section>
</template>

<script charset="utf-8">
    import SessionVisualization from "@/components/Visualization/SessionVisualization.vue";
    import VisualizationHeader from "@/components/Visualization/VisualizationHeader.vue";
    import PublicService from "@/services/public.service";
    import esesjaInteractivePublicService from "@/services/esesja.interactive.public.service";
    import { loadOwLanguage } from "@/helpers/ow.language.helper";
    import { isVisualizationCodeWellFormed } from "@/helpers/ow.route.helper";
    import { getRetryNotBefore } from "@/helpers/retry.after.helper";

    // Jak szybko okno zauważa uruchomienie, zmianę i zakończenie posiedzenia - tak jak nakładka transmisyjna.
    const ACTIVATION_CHECK_INTERVAL_MS = 10000;
    /*
     * Po 404 okno sprawdza rzadziej. Adres okna ustala klient i może go zmienić, więc 404 zwykle znaczy
     * "ten adres już nie istnieje", a nie "zaraz zacznie działać". Odpytywanie co 10 s zostawionej zakładki
     * zasypywałoby historię aktywności wpisami UnknownLink i zjadało budżet limitera jednostki - a ten liczy
     * teraz także żądania z błędnym adresem. Link naprawiony po stronie panelu i tak wróci, tylko wolniej.
     */
    const INVALID_LINK_CHECK_INTERVAL_MS = 300000;

    /**
     * Okno wizualizacji pod stałym linkiem jednostki (/Visualization/:councilId/:code). Samo ustala, które posiedzenie
     * jest uruchomione (wpis w aktywna_sesja), i prowadzi stany bez posiedzenia. Prezentację i SignalR prowadzi
     * SessionVisualization - ta sama, co w oknie konkretnego posiedzenia (/PublicSession/:code).
     */
    export default {
        name: "VisualizationView",
        components: {
            SessionVisualization,
            VisualizationHeader
        },
        data() {
            return {
                code: "",
                councilId: 0,
                // loading | waiting | active | unavailable | invalid
                status: "loading",
                // sprawdzenie nie doszło do serwera - prezentacja zostaje, ale może być nieaktualna
                isConnectionLost: false,
                clientName: "",
                presentation: null,
                // aktywacja pokazywana przez prezentację: posiedzenie + wpis w aktywna_sesja + czas uruchomienia
                presentationKey: "",
                checkTimer: null,
                checkPromise: null,
                requestSequence: 0,
                appliedSequence: 0,
                // po odpowiedzi 429 (limit zapytań jednostki): najwcześniejszy termin kolejnego pobrania stanu
                // w ms od epoki; 0 - bez przerwy
                retryNotBefore: 0,
                isUnmounted: false
            };
        },
        computed: {
            getLangCode() {
                return this.$route.query.lang;
            },
            statusMessage() {
                switch (this.status) {
                    case "waiting":
                        return this.$t('com.file.visualization.no-active-session');
                    case "unavailable":
                        return this.$t('com.validate-error.no-session-or-hide');
                    case "invalid":
                        return this.$t('com.validate-error.invalid-identification');
                    default:
                        return "";
                }
            }
        },
        async created() {
            try {
                this.$i18n.locale = await loadOwLanguage(this.getLangCode);
            }
            catch (e) {
                console.log(e);
            }
        },
        mounted() {
            this.code = this.$route.params.code;
            this.councilId = Number(this.$route.params.councilId);

            if (!(this.councilId > 0) || !isVisualizationCodeWellFormed(this.code)) {
                this.status = "invalid";
                return;
            }

            document.addEventListener("visibilitychange", this.handleConnectionResume);
            window.addEventListener("online", this.handleConnectionResume);
            window.addEventListener("pageshow", this.handleConnectionResume);

            this.$store.commit("showLoader");
            this.checkActivation().finally(() => {
                this.$store.commit("hideLoader");
            });
        },
        unmounted() {
            this.isUnmounted = true;
            clearTimeout(this.checkTimer);
            document.removeEventListener("visibilitychange", this.handleConnectionResume);
            window.removeEventListener("online", this.handleConnectionResume);
            window.removeEventListener("pageshow", this.handleConnectionResume);
            esesjaInteractivePublicService.stop().catch((e) => {
                console.log(e);
            });
        },
        methods: {
            handleConnectionResume() {
                if (document.hidden) {
                    return;
                }

                // Wybudzenie strony lub powrót sieci: najpierw ustalamy aktualne posiedzenie. Do grupy SignalR
                // prezentacja wraca dopiero po potwierdzeniu, że jej aktywacja jest nadal aktualna.
                this.checkActivation();
            },
            /**
             * Jedno sprawdzenie naraz - wywołania w trakcie dostają to samo. Kolejne planowane dopiero po
             * zakończeniu poprzedniego, więc żądania się nie nakładają. Zwraca odpowiedź albo null przy błędzie.
             */
            checkActivation() {
                if (this.checkPromise !== null) {
                    return this.checkPromise;
                }

                // Po 429 do terminu z Retry-After nie wychodzi żadne pobranie stanu - ani planowe, ani po wybudzeniu
                // strony czy powrocie sieci, ani potwierdzenie celu przed wejściem do grupy SignalR. Wynik jest wtedy
                // nieznany (null), a nie "brak posiedzenia", więc prezentacja zostaje.
                if (this.isRetryDeferred()) {
                    if (this.checkTimer === null) {
                        this.scheduleCheck(this.retryNotBefore - Date.now());
                    }

                    return Promise.resolve(null);
                }

                clearTimeout(this.checkTimer);
                this.checkTimer = null;

                const sequence = ++this.requestSequence;

                this.checkPromise = this.fetchVisualization()
                    .then((data) => {
                        this.applyVisualization(sequence, data);
                        return data;
                    })
                    .catch((error) => {
                        this.applyError(sequence, error);
                        return null;
                    })
                    .finally(() => {
                        this.checkPromise = null;
                        this.scheduleNextCheck();
                    });

                return this.checkPromise;
            },
            scheduleNextCheck() {
                // zwykły rytm odpytywania, ale nie wcześniej, niż pozwolił serwer po 429
                this.scheduleCheck(Math.max(ACTIVATION_CHECK_INTERVAL_MS, this.retryNotBefore - Date.now()));
            },
            scheduleCheck(delay) {
                if (this.isUnmounted) {
                    return;
                }

                clearTimeout(this.checkTimer);
                this.checkTimer = setTimeout(() => {
                    this.checkTimer = null;
                    this.checkActivation();
                }, delay);
            },
            isRetryDeferred() {
                return this.retryNotBefore > Date.now();
            },
            async fetchVisualization() {
                const response = await PublicService.getVisualizationSession(this.councilId, this.code, this.$i18n.locale);
                return response.data;
            },
            activationKeyOf(data) {
                return `${data.sessionId}|${data.activationId}|${data.activatedAt}`;
            },
            applyVisualization(sequence, data) {
                // spóźniona odpowiedź - nowsze żądanie zostało już zastosowane
                if (this.isUnmounted || sequence < this.appliedSequence) {
                    return;
                }

                this.appliedSequence = sequence;
                // serwer znów obsługuje zapytania - koniec przerwy po 429
                this.retryNotBefore = 0;

                const wasConnectionLost = this.isConnectionLost;
                this.isConnectionLost = false;
                this.clientName = data !== null && data !== undefined ? (data.clientName || "") : "";

                if (data === null || data === undefined || data.isActive !== true || data.data === null || data.data === undefined) {
                    this.endPresentation();
                    this.status = "waiting";
                    return;
                }

                const key = this.activationKeyOf(data);

                if (key !== this.presentationKey) {
                    // Inne posiedzenie albo ponowne uruchomienie tego samego: zmiana klucza najpierw kończy
                    // poprzednią prezentację (zdarzenia, grupa SignalR, Vuex), a dopiero potem startuje nowa.
                    this.presentation = data.data;
                    this.presentationKey = key;
                    this.status = "active";
                    return;
                }

                // Ta sama aktywacja - bez ponownej hydratacji, żeby nie kasować stanu sterowanego zdarzeniami.
                // Wyjątek: po utracie połączenia stan trzeba odbudować, bo zdarzenia z przerwy przepadły.
                if (wasConnectionLost && this.$refs.presentation) {
                    this.$refs.presentation.recoverConnection().catch((e) => {
                        console.log(e);
                    });
                }
            },
            applyError(sequence, error) {
                if (this.isUnmounted || sequence < this.appliedSequence) {
                    return;
                }

                this.appliedSequence = sequence;

                const status = error !== null && error !== undefined && error.response ? error.response.status : undefined;

                if (status === 404) {
                    // Nieznany adres. Sprawdzanie trwa dalej, żeby okno wróciło samo, gdy link znów zacznie
                    // działać, ale w znacznie wolniejszym rytmie - patrz INVALID_LINK_CHECK_INTERVAL_MS.
                    this.endPresentation();
                    this.isConnectionLost = false;
                    this.retryNotBefore = Date.now() + INVALID_LINK_CHECK_INTERVAL_MS;
                    this.status = "invalid";
                    return;
                }

                if (status === 403) {
                    // Uruchomione posiedzenie nie spełnia warunków okna wizualizacji: dane znikają z ekranu,
                    // a kolejne sprawdzenia wykryją następne dostępne posiedzenie.
                    this.endPresentation();
                    this.isConnectionLost = false;
                    this.retryNotBefore = 0;
                    this.status = "unavailable";
                    return;
                }

                if (status === 429) {
                    // Limit zapytań jednostki: jak przy utracie połączenia prezentacja zostaje z informacją, że może
                    // być nieaktualna, a kolejne pobranie wychodzi dopiero po terminie z Retry-After. Pierwsze udane
                    // sprawdzenie po przerwie odbuduje stan, bo zdarzenia z przerwy mogły nie zostać zastosowane.
                    this.retryNotBefore = getRetryNotBefore(error, ACTIVATION_CHECK_INTERVAL_MS);
                    this.isConnectionLost = true;
                    return;
                }

                // Sieć albo błąd serwera to nie jest "brak posiedzenia". Prezentacja zostaje, a informacja
                // o utracie aktualizacji wisi do pierwszego udanego sprawdzenia.
                console.log(error);
                this.isConnectionLost = true;
            },
            endPresentation() {
                this.presentation = null;
                this.presentationKey = "";
            },
            /**
             * Dane pokazywanej aktywacji dla SessionVisualization (zmiana porządku obrad, odbudowa po rozłączeniu).
             * null, gdy w międzyczasie zmieniło się posiedzenie - wtedy okno samo przełącza prezentację.
             */
            async loadPresentedSession() {
                // W przerwie po 429 bez pobrania: dane nie są stosowane (null). Okno jest wtedy oznaczone jako
                // nieaktualne, więc pierwsze udane sprawdzenie po przerwie i tak odbuduje pełny stan prezentacji.
                if (this.isRetryDeferred()) {
                    return null;
                }

                const expectedKey = this.presentationKey;
                const sequence = ++this.requestSequence;
                let data;

                try {
                    data = await this.fetchVisualization();
                }
                catch (error) {
                    this.applyError(sequence, error);
                    throw error;
                }

                this.applyVisualization(sequence, data);

                if (this.isUnmounted || expectedKey === "" || this.presentationKey !== expectedKey) {
                    return null;
                }

                return data.data;
            },
            /**
             * Potwierdzenie przed ponownym wejściem do grupy SignalR: pokazywana aktywacja jest wciąż aktualna.
             */
            async confirmPresentedTarget() {
                const expectedKey = this.presentationKey;
                const data = await this.checkActivation();

                return data !== null && !this.isUnmounted && expectedKey !== "" && this.presentationKey === expectedKey;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .visualization-idle__body {
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        min-height: calc(100vh - 140px);
        padding: 48px;
    }

    .visualization-idle__message {
        margin: 0px;
        font-size: 42px;
        font-weight: 600;
        line-height: 128%;
        color: #00082c;
        text-align: center;
    }

    .visualization-connection-lost {
        position: fixed;
        left: 50%;
        bottom: 24px;
        transform: translateX(-50%);
        z-index: 1000;
        box-sizing: border-box;
        max-width: calc(100% - 48px);
        padding: 12px 24px;
        border-radius: 8px;
        background: #e40030;
        color: #ffffff;
        font-size: 18px;
        font-weight: 500;
        text-align: center;
        box-shadow: 0px 6px 24px rgba(0, 8, 44, 0.25);
    }
</style>
